import { invalidatePolicyQueries } from '@/utils/policy-cache'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { Button } from '@deepguide-ai/ui/button'
import { SearchableSelect } from '@deepguide-ai/ui/combobox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Plus, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import PageLoading from '@/components/PageLoading'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { ImportPolicyDialog } from '@/components/screenata/ImportPolicyDialog'
import { ScreenataPoliciesContent } from '@/components/screenata/ScreenataPoliciesContent'
import { Input } from '@deepguide-ai/ui/input'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/policies'
)({
  component: ScreenataPoliciesPage,
  staticData: { pageHeader: { title: 'Policies', pad: false } },
})

function ScreenataPoliciesPage() {
  const { workspaceId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/policies',
  })
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [importOpen, setImportOpen] = useState(false)
  const [newPolicyOpen, setNewPolicyOpen] = useState(false)
  const { canWorkspaceWrite: canWritePolicies } =
    useWorkspacePermissions(workspaceId)

  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions()
  )

  // RFC-160 Phase 0: generation runs automatically in the background
  // after onboarding completes (enqueued from `completeOnboarding`).
  // This page polls so per-row `generationState` stays fresh while any
  // policy is still queued or generating.
  useQuery({
    // Not framework-gated — poll generation state across all enrolled
    // frameworks' policies.
    ...trpc.complianceProgram.getGenerationItems.queryOptions(),
    refetchInterval: (query) => {
      const items = ((query.state.data as any)?.items ?? []) as Array<{
        category?: string
        generationState?: string | null
      }>
      const actionable = items.filter((i) => i.category !== 'deliverable')
      if (actionable.length === 0) return false
      const stillRunning = actionable.some(
        (i) =>
          i.generationState === 'queued' || i.generationState === 'generating'
      )
      return stillRunning ? 3000 : false
    },
  })
  // Preserve queryClient read for consistent cache priming across
  // mounts — the hook itself isn't referenced downstream.
  void queryClient

  if (programQuery.isLoading) return <PageLoading />

  // RFC-163: Policy Prep is no longer a gated onboarding step.
  // The policies page is a main-menu surface; users land here from the
  // sidebar after finishing the wizard and can review / approve any
  // time. No onboarding-mode footer, no "Continue to Control Review"
  // button, no completeOnboardingStep call — policies regenerate and
  // re-attest on their own cadence after they're edited.
  const programId = (programQuery.data as any)?.id ?? ''

  const invalidateAll = () => invalidatePolicyQueries(queryClient)

  const handleImported = async (importedSlugs: string[]) => {
    await invalidateAll()
    // Single import → jump straight to the policy. Bulk → stay on the
    // list (now refreshed) so the user sees everything they brought in.
    if (importedSlugs.length === 1) {
      navigate({
        to: '/workspaces/$workspaceId/policies/$policySlug',
        params: { workspaceId, policySlug: importedSlugs[0] },
      })
    }
  }

  return (
    <>
      {canWritePolicies && (
        <PageHeaderActions>
          <button
            type='button'
            onClick={() => setImportOpen(true)}
            className='font-body border-border-divider hover:bg-surface-subtle inline-flex h-8 items-center gap-1.5 rounded-sm border bg-card px-3 text-[12px] font-medium text-[#3F3F39] transition-colors dark:text-text-body'
          >
            <Upload className='h-3.5 w-3.5' />
            Import
          </button>
          <Button
            type='button'
            size='sm'
            onClick={() => setNewPolicyOpen(true)}
            className='font-body h-8 text-[12px]'
          >
            <Plus className='h-3.5 w-3.5' />
            New
          </Button>
        </PageHeaderActions>
      )}
      <ScreenataPoliciesContent
          workspaceId={workspaceId} />
      <ImportPolicyDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        programId={programId}
        onImported={handleImported}
      />
      <NewPolicyDialog
        open={newPolicyOpen}
        onOpenChange={setNewPolicyOpen}
        programId={programId}
        workspaceId={workspaceId}
        onCreated={invalidateAll}
      />
    </>
  )
}

function NewPolicyDialog({
  open,
  onOpenChange,
  programId,
  workspaceId,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  programId: string
  workspaceId: string
  onCreated: () => Promise<void>
}) {
  const [slug, setSlug] = useState('')
  const [customTitle, setCustomTitle] = useState('')
  const navigate = useNavigate()

  // Offer policy slugs from every enrolled framework (not framework-gated).
  const programQuery = useQuery({
    ...trpc.complianceProgram.getGenerationItems.queryOptions(),
    staleTime: 10 * 60 * 1000,
  })
  const slugOptions = useMemo(() => {
    const items = ((programQuery.data as any)?.items ?? []) as Array<{
      slug?: string
      title?: string
      isGenerated?: boolean
    }>
    return items
      .filter((i) => i.slug && i.title)
      .map((i) => ({ slug: i.slug as string, title: i.title as string }))
  }, [programQuery.data])

  const createMutation = useMutation(
    trpc.policyImport.createBlank.mutationOptions()
  )

  const reset = () => {
    setSlug('')
    setCustomTitle('')
  }

  const handleCreate = () => {
    if (!programId) return
    const isCustom = slug === '__custom__'
    if (isCustom && !customTitle.trim()) {
      toast.error('Enter a policy title')
      return
    }
    if (!isCustom && !slug) {
      toast.error('Select a policy')
      return
    }
    createMutation.mutate(
      {
        programId,
        slug: isCustom ? undefined : slug,
        customTitle: isCustom ? customTitle.trim() : undefined,
      },
      {
        onSuccess: async (data) => {
          await onCreated()
          onOpenChange(false)
          reset()
          navigate({
            to: '/workspaces/$workspaceId/policies/$policySlug',
            params: { workspaceId, policySlug: data.slug },
          })
        },
        onError: (err: Error) =>
          toast.error(err.message ?? 'Failed to create policy'),
      }
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset()
        onOpenChange(v)
      }}
    >
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle className='font-heading text-[16px] font-semibold'>
            New Policy
          </DialogTitle>
        </DialogHeader>
        <div className='flex flex-col gap-4 px-6 py-2'>
          <div className='flex flex-col gap-1.5'>
            <label className='font-body text-text-body text-[12px] font-medium'>
              Policy
            </label>
            <SearchableSelect
              options={[
                ...slugOptions.map((option) => ({
                  label: option.title,
                  value: option.slug,
                })),
                { label: '+ Custom policy', value: '__custom__' },
              ]}
              type='a policy'
              value={slug}
              onValueChange={setSlug}
              placeholder='Select a policy…'
              searchPlaceholder='Search policies…'
              emptyMessage='No policies found.'
              portalled={false}
              triggerProps={{
                className:
                  'border-border-divider h-9 w-full justify-between text-[12px]',
              }}
            />
          </div>

          {slug === '__custom__' && (
            <div className='flex flex-col gap-1.5'>
              <label className='font-body text-text-body text-[12px] font-medium'>
                Policy title
              </label>
              <Input
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder='e.g. "Disaster Recovery Plan"'
                className='font-body border-border-divider text-text-secondary placeholder:text-text-placeholder h-9 w-full rounded-md border px-3 text-[12px] focus:border-ring focus:outline-none'
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant='outline'
            size='sm'
            className='border-border-divider h-8 rounded-sm text-[12px]'
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>
          <Button
            size='sm'
            className='bg-primary text-primary-foreground hover:bg-primary/90 h-8 rounded-sm text-[12px]'
            onClick={handleCreate}
            disabled={createMutation.isPending || (!slug && !customTitle)}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
